import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SiteNav from '../components/SiteNav';
import PageMeta from '../components/PageMeta';
import { Check, ArrowRight, Clock, Heart, Loader2, Users, Minus, Plus, Sparkles, Award, Shield } from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';
import { isPlayBillingAvailable, isPlayBillingReady, purchasePlusPlan } from '../utils/googlePlayBilling';
import { getDisplayCurrency, getLocalPrice } from '../utils/displayCurrency';
import useAdultMode from '../hooks/useAdultMode';
import useCreditsLabel from '../hooks/useCreditsLabel';
import { useLanguage } from '../contexts/LanguageContext';
import PlanComparisonTable from '../components/billing/PlanComparisonTable';
import MeetTheAvAs from '../components/MeetTheAvAs';
// Dashboard pricing — IDENTICAL card markup to the public Homepage
// (`/app/frontend/src/pages/Homepage.js` § Section 12). The whole point is
// that families see the SAME 4 cards (FIRST STEPS · STEADY · TOGETHER ·
// BRAVE) on both sides of login, with the only dashboard-specific extra
// being the live multi-teen total breakdown.
// ─────────────────────────────────────────────────────────────────────────

// Public shell — clean marketing header/footer for logged-out visitors
// (mirrors the /schools page chrome) so anonymous users never see the
// dashboard sidebar + dummy avatar.
const PublicPricingShell = ({ children }) => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FFFDF9' }} data-testid="public-pricing-shell">
      <PageMeta
        title="HiBFF Pricing — Family Plans for Teen Social Confidence"
        description="Simple family pricing for HiBFF: start free, then choose Steady, Together or Brave. Parents stay in control of billing; teens get safe AI conversation practice."
        path="/pricing"
        slug="pricing"
      />
      <SiteNav current="pricing" mode="sticky" />
      {children}
      <footer className="py-10 px-4 bg-gray-900 text-white" data-testid="pricing-footer">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-8">
            <div>
              <h4 className="font-bold mb-3">Product</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/register?persona=teen" className="hover:text-white">Teens</a></li>
                <li><a href="/parent-first" className="hover:text-white">Parents</a></li>
                <li><a href="/register?persona=adult" className="hover:text-white">Adults</a></li>
                <li><a href="/education" className="hover:text-white">Education</a></li>
                <li><a href="/pricing" className="hover:text-white">Pricing</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-3">Company</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/about" className="hover:text-white">About</a></li>
                <li><a href="/investor-portal" className="hover:text-white">Investor Portal</a></li>
                <li><a href="/shop" className="hover:text-white">Shop</a></li>
                <li><a href="/partners" className="hover:text-white">Partners</a></li>
                <li><a href="mailto:careers@hibff.com" className="hover:text-white">Careers</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-3">For Parents</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/teen-safety" className="hover:text-white">Teen Safety &amp; Privacy</a></li>
                <li><a href="/trust-safety" className="hover:text-white">Trust &amp; Safety — Live Numbers</a></li>
                <li><a href="/docs" className="hover:text-white">Parent Dashboard Guide</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-3">Legal</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/terms" className="hover:text-white">Terms &amp; Conditions</a></li>
                <li><a href="/privacy" className="hover:text-white">Privacy &amp; Data Safety Policy</a></li>
                <li><a href="/refund-policy" className="hover:text-white">Refund Policy</a></li>
                <li><a href="/pause-policy-90" className="hover:text-white">90 Day Pause Policy</a></li>
                <li><a href="/pause-policy-indefinite" className="hover:text-white">Indefinite Pause Policy</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-3">Contact</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="mailto:parents@hibff.com" className="hover:text-white">parents@hibff.com</a></li>
                <li><a href="mailto:schools@hibff.com" className="hover:text-white">schools@hibff.com</a></li>
                <li><a href="mailto:business@hibff.com" className="hover:text-white">business@hibff.com</a></li>
                <li><a href="mailto:support@hibff.com" className="hover:text-white">support@hibff.com</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-6 border-t border-gray-800 text-center text-xs text-gray-500">
            <p>HiBFF — Hi Brave Friends Forever · Prices shown in {getDisplayCurrency().code} — charged by Stripe at secure checkout</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

const PricingPage = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdultMode = useAdultMode();
  const { label: creditsLabel } = useCreditsLabel();
  const [apiPlans, setApiPlans] = useState(null);
  const [familyPricingMeta, setFamilyPricingMeta] = useState(null);
  const [teenCount, setTeenCount] = useState(1);
  const [livePricing, setLivePricing] = useState({});  // { plan_id: breakdown }
  const [subscribing, setSubscribing] = useState(null);
  const [subscribeResult, setSubscribeResult] = useState(null);
  const [startingTrial, setStartingTrial] = useState(false);
  const [communityCode, setCommunityCode] = useState('');
  const [communityStatus, setCommunityStatus] = useState(null); // {valid, city, percent_off} | {error}
  const [founding, setFounding] = useState(null); // Founding Families launch offer status

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/campaign/founding-families`)
      .then(r => r.json()).then(setFounding).catch(() => {});
  }, []);
  // Parent-onboarding hand-off: /pricing?recommend=plus_monthly highlights the suggested family plan.
  const recommendedPlanId = new URLSearchParams(window.location.search).get('recommend');
  const { t } = useLanguage();

  const checkCommunityCode = async () => {
    const code = communityCode.trim().toUpperCase();
    if (!code) { setCommunityStatus(null); return; }
    try {
      const r = await apiClient.request(`/api/school-licensing/community-code/${encodeURIComponent(code)}`);
      setCommunityStatus(r);
    } catch {
      setCommunityStatus({ error: 'Code not recognised or no longer active' });
    }
  };

  useEffect(() => {
    apiClient.request('/api/billing/plans')
      .then(data => {
        setApiPlans(data.plans || []);
        setFamilyPricingMeta(data.family_pricing || null);
      })
      .catch(() => setApiPlans(null));

    apiClient.request('/api/parent/family')
      .then(data => {
        if (data?.teen_count > 0) setTeenCount(Math.max(1, data.teen_count));
      })
      .catch(() => {});
  }, []);

  // Refresh live multi-teen pricing whenever teenCount changes
  useEffect(() => {
    if (!apiPlans) return;
    const fetchPricing = async () => {
      const next = {};
      for (const p of apiPlans) {
        try {
          const r = await apiClient.request(`/api/billing/family-pricing?plan_id=${p.id}&teens=${teenCount}`);
          next[p.id] = r;
        } catch { /* ignore */ }
      }
      setLivePricing(next);
    };
    fetchPricing();
  }, [teenCount, apiPlans]);

  // Resolve the user's current plan to a HOMEPAGE_PLANS id. Uses the
  // canonical `subscription_tier` enum value as the primary signal (single
  // source of truth — matches backend get_subscription_tier_name() and the
  // HOMEPAGE_PLANS ids below), and only falls back to fuzzy matching the
  // display name when the canonical id is absent (legacy V1 users).
  const getUserPlanId = () => {
    if (!user) return null;
    const tier = (user.subscription_tier || '').toLowerCase();
    if (tier === 'plus_annual') return 'plus_annual';
    if (tier === 'plus_monthly' || tier === 'plus_trial') return 'plus_monthly';
    if (tier === 'starter') return 'starter';
    if (tier === 'free') return 'free';
    // Fallback: legacy users without a clean enum value — use the display
    // name. Order matters: 'master'/'elite' first because they are the
    // tiers we most want to recognise correctly.
    const tierName = (user.subscription_tier_name || '').toLowerCase();
    if (tierName.includes('master') || tierName.includes('brave')) return 'plus_annual';
    if (tierName.includes('elite') || tierName.includes('together') || tierName.includes('plus')) return 'plus_monthly';
    if (tierName.includes('challenger') || tierName.includes('steady') || tierName.includes('starter')) return 'starter';
    return 'free';
  };

  const currentPlanId = getUserPlanId();

  // Production users have no `role` field — detect teens by any signal.
  const isTeenAccount = (
    (user?.role || '').toLowerCase() === 'teen'
    || (user?.age_band || '').toLowerCase() === 'teen'
    || (Number(user?.age) > 0 && Number(user?.age) < 18)
  );

  const showResult = (result) => {
    setSubscribeResult(result);
    setTimeout(() => {
      document.querySelector('[data-testid="subscribe-result"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const handleSubscribe = async (planId) => {
    if (!user) { navigate(`/register?plan=${encodeURIComponent(planId)}`); return; }
    if (planId === currentPlanId) return;
    setSubscribing(planId);
    setSubscribeResult(null);
    try {
      // Sprint 207d/e — Compliance gate. Teens NEVER reach Stripe directly.
      // For a teen, send a generic upgrade-interest request — the parent
      // picks the plan and pays from their own card on the landing page.
      // Sprint 207d/e — Compliance gate. Teens NEVER reach Stripe directly.
      // For a teen, send a generic upgrade-interest request — the parent
      // picks the plan and pays from their own card on the landing page.
      if (isTeenAccount && planId !== 'free') {
        const res = await apiClient.request('/api/parent-upgrade/request', {
          method: 'POST',
          body: JSON.stringify({
            // Don't lock the parent into the teen's pick — parent chooses.
            // We pass the teen's tap only as a soft "suggested" signal.
            plan_id: planId === 'teen_generic_upgrade' ? null : planId,
            teen_count: teenCount,
            origin_url: window.location.origin,
          }),
        });
        if (res?.status === 'awaiting_parent_approval') {
          const phoneBit = res.parent_masked_phone ? ` at ${res.parent_masked_phone}` : '';
          const nameBit = res.parent_name ? ` (${res.parent_name})` : '';
          showResult({
            type: 'success',
            message: `Texted your parent${nameBit}${phoneBit}. They'll pick a plan and pay from their card — not yours.`,
          });
        } else {
          showResult({ type: 'error', message: 'Could not send the parent approval request. Please try again.' });
        }
        setSubscribing(null);
        return;
      }

      if (isPlayBillingAvailable()) {
        // ALL paid plans must go through Google Play Billing on Android (Play policy).
        // NEVER fall back to Stripe inside the native app — that violates Play rules.
        if (!(await isPlayBillingReady())) {
          showResult({
            type: 'error',
            message: 'In-app purchases are not available on this version of the app. Please update HiBFF from Google Play and try again.',
          });
          setSubscribing(null);
          return;
        }
        const interval = planId === 'plus_annual' ? 'annual' : planId === 'starter' ? 'starter' : 'monthly';
        const res = await purchasePlusPlan(interval);
        if (res.status === 'active') {
          showResult({ type: 'success', message: res.message || 'Plan activated!' });
          setTimeout(() => window.location.reload(), 1500);
        } else if (res.status === 'cancelled') {
          showResult({ type: 'error', message: 'Purchase cancelled' });
        } else {
          showResult({ type: 'error', message: res.message || 'Subscription failed' });
        }
      } else {
        const res = await apiClient.request('/api/billing/checkout/subscription', {
          method: 'POST',
          body: JSON.stringify({
            plan_id: planId,
            origin_url: window.location.origin,
            community_code: communityStatus?.valid ? communityCode.trim().toUpperCase() : null,
          }),
        });
        if (res.free) {
          setSubscribeResult({ type: 'success', message: `${res.plan} activated.` });
          setTimeout(() => window.location.reload(), 1200);
        } else if (res.url) {
          window.location.href = res.url;
        } else {
          setSubscribeResult({ type: 'error', message: 'Could not start checkout. Please try again.' });
        }
      }
    } catch (err) {
      const msg = err?.message || 'Subscription failed';
      // Server-side teen gate (403 parent_approval_required) — show it clearly.
      showResult({ type: 'error', message: msg });
    }
    setSubscribing(null);
  };

  const handleStartTrial = async () => {
    setStartingTrial(true);
    setSubscribeResult(null);
    try {
      const res = await apiClient.request('/api/enhancements/start-trial', {
        method: 'POST',
        body: JSON.stringify({ plan_id: 'plus_monthly' }),
      });
      setSubscribeResult({ type: 'success', message: res.message || 'Trial started!' });
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      setSubscribeResult({ type: 'error', message: err.message || 'Could not start trial' });
    }
    setStartingTrial(false);
  };

  const canTrial = user && !user.trial_used && (!user.subscription_tier || user.subscription_tier === 'free' || user.subscription_tier === 'starter');

  // EXACT same plan data shape as Homepage.js § Section 12 (Families).
  // Mirrors badge label, badge style, tagline, price, period, sub, extraTeen,
  // features (plain strings), excluded list, cta and ctaStyle.
  const ADULT_PLANS = [
    { id: 'free',     name: 'ENTRY',     tagline: 'Try Your First Rep',         price: `${getDisplayCurrency().symbol}0`,   period: '/forever',
      badgeLabel: 'ENTRY',     badgeStyle: 'bg-emerald-100 text-emerald-700 border border-emerald-300',
      features: ['Default practice AvA', '3 starter scenarios', '15 FREE practice minutes', 'Adult-only safety guardrails'],
      excluded: ['Custom practice AvA', 'Voice messaging', 'Career playbook library'],
      cta: 'Start Free — No Card', ctaStyle: 'bg-emerald-600 text-white hover:bg-emerald-700', free: true },
    { id: 'adult_pro_monthly',      name: 'PRO',       tagline: 'Practise to Win',            price: `${getDisplayCurrency().symbol}19`,  period: '/mo',
      badgeLabel: 'PRO',       badgeStyle: 'bg-amber-100 text-amber-700 border border-amber-300',
      features: ['1 personalised practice AvA', 'Full career playbook library', 'Unlimited text messages', 'Light voice (15 mins/mo)', 'Interview & negotiation packs'],
      excluded: ['Multiple personas', 'Advanced replay analytics'],
      cta: 'Upgrade to Pro', ctaStyle: 'bg-amber-50 text-amber-700 border-2 border-amber-500 hover:bg-amber-100' },
    { id: 'adult_executive_monthly',name: 'EXECUTIVE', tagline: 'Boardroom-Ready Confidence', price: `${getDisplayCurrency().symbol}49`,  period: '/mo',
      badgeLabel: 'EXECUTIVE', badgeStyle: 'bg-orange-500 text-white',
      features: ['3 practice AvAs (work / dating / family)', 'Every scenario unlocked', 'Unlimited text', '180 voice minutes / month', 'Replay analytics + tone scoring', 'Stripe invoiced billing'],
      cta: 'Upgrade to Executive', ctaStyle: 'bg-orange-500 text-white hover:bg-orange-600', popular: true },
    { id: 'adult_master_annual',    name: 'MASTER ANNUAL', tagline: 'Annual — Save 33%',       price: `${getDisplayCurrency().symbol}390`, period: '/year', sub: `Only ${getDisplayCurrency().symbol}32.50/mo billed annually`,
      badgeLabel: 'MASTER ANNUAL', badgeStyle: 'bg-[#7B4FD8] text-white',
      features: ['5 practice AvAs', 'Everything in Executive', '360 voice minutes / month', 'Priority human coach review', 'Same 30-day refund + pause options'],
      cta: 'Upgrade to Master Annual', ctaStyle: 'bg-[#7B4FD8] text-white hover:bg-[#6A3FC7]', annual: true },
  ];

  const HOMEPAGE_PLANS = [
    {
      id: 'free',
      name: 'First Steps',
      tagline: 'A taste of HiBFF',
      price: `${getDisplayCurrency().symbol}0`,
      period: '/forever',
      badgeLabel: 'FIRST STEPS',
      badgeStyle: 'bg-emerald-100 text-emerald-700 border border-emerald-300',
      features: [
        'Meet 3 of our 9 AvAs — Rebecca (Knowledge Tutor), Chloe (Friendships) & Marcus (Careers & School)',
        '10 FREE voice minutes — just enough to experience the magic',
        'Pay as you go — buy Heart time credits with your own card, whenever you need them (challenges earn virtual badges only)',
      ],
      cta: 'Start Free — No Card',
      ctaStyle: 'bg-emerald-600 text-white hover:bg-emerald-700',
      free: true,
    },
    {
      id: 'starter',
      name: 'Steady',
      tagline: 'One teen, building confidence',
      price: getLocalPrice('steady').text,
      period: '/mo',
      badgeLabel: 'STEADY',
      badgeStyle: 'bg-purple-100 text-purple-700 border border-purple-300',
      features: [
        '6 of the 9 AvAs to practise with',
        'Neo — Your Friend (customise name, voice, image, and backstory)',
        'Challenges 1-10 (Virtual Badges)',
        'Unlimited text messages',
        '45 voice minutes / month',
        'Confidence tracking + insights',
      ],
      excluded: ['Challenges 11-20', 'Achievement NFTs', 'Parent/Teen shared access'],
      cta: 'Upgrade to Steady',
      ctaStyle: 'bg-purple-50 text-[#7B4FD8] border-2 border-[#7B4FD8] hover:bg-purple-100',
    },
    {
      id: 'plus_monthly',
      name: 'Together',
      tagline: 'Families & growth',
      price: getLocalPrice('together').text,
      period: '/mo',
      badgeLabel: 'TOGETHER',
      badgeStyle: 'bg-[#7B4FD8] text-white',
      features: [
        'All 9 AvAs — the complete tutor team',
        'Challenges 1-20 (Virtual Badges + NFT Achievements at #10)',
        'Unlimited text',
        '180 voice minutes / month',
        'Parent/Teen shared (up to 2 teens)',
        'AI-generated rewards in the 180-Day Vault (parent-authorised)',
        '30-day refund + 90-day pause + indefinite pause options',
      ],
      cta: 'Upgrade to Together',
      ctaStyle: 'bg-[#7B4FD8] text-white hover:bg-[#6A3FC7]',
      popular: true,
    },
    {
      id: 'plus_annual',
      name: 'Brave',
      tagline: 'Full commitment',
      price: getLocalPrice('brave').text,
      period: '/year',
      badgeLabel: 'BRAVE ★ BEST VALUE',
      badgeStyle: 'bg-[#F5831F] text-white',
      features: [
        'Every AvA + early access',
        'All challenges + early access',
        '300 voice minutes / month (120 bonus vs monthly)',
        'Everything in Together',
        'Priority support',
        'Same refund / pause options',
      ],
      cta: 'Upgrade to Brave',
      ctaStyle: 'bg-[#F5831F] text-white hover:bg-[#E07318]',
      annual: true,
    },
  ];

  const fmtMoney = (cents, currency, interval) => {
    const symbol = currency === 'GBP' ? '\u00A3' : currency === 'EUR' ? '\u20AC' : '$';
    const amount = (cents ?? 0) / 100;
    return `${symbol}${amount.toFixed(amount % 1 === 0 ? 0 : 2)}${interval === 'year' ? '/year' : '/mo'}`;
  };

  const Wrapper = embedded ? React.Fragment : (user ? DashboardLayout : PublicPricingShell);

  return (
    <Wrapper>
      <div className="p-4 sm:p-8 max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3" data-testid="pricing-title">{user ? 'My Plan' : 'Simple Pricing'}</h1>
          <p className="text-base text-gray-600 max-w-xl mx-auto">
            {currentPlanId && currentPlanId !== 'free'
              ? `You're currently on ${HOMEPAGE_PLANS.find(p => p.id === currentPlanId)?.name || 'your plan'}. Upgrade anytime for more practice time.`
              : 'Start free. Grow together. Go Brave.'}
          </p>
          <p className="text-xs text-gray-400 mt-2" data-testid="pricing-currency-note">
            Prices shown in your local currency where available · everyone else is charged their local equivalent automatically at secure checkout
          </p>
          <p className="text-xs text-gray-500 mt-1" data-testid="pricing-adults-note">
            Teens and adults pay the same — adult accounts simply skip the parental layer
          </p>
          <p className="text-sm font-bold text-gray-900 flex items-center justify-center gap-1.5 mt-4" data-testid="pricing-data-pledge">
            <Shield size={15} style={{ color: '#059669' }} />
            Your data belongs to you. No advertising.{' '}
            <a href="/privacy" className="text-purple-700 underline decoration-2 underline-offset-2 hover:text-purple-900 font-semibold" data-testid="pricing-data-pledge-link">Our policy</a>
          </p>
        </div>

        {subscribeResult && (
          <div className={`mb-6 p-4 rounded-xl text-center font-medium ${subscribeResult.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`} data-testid="subscribe-result">
            {subscribeResult.message}
          </div>
        )}

        {/* Sprint 207d — Compliance banner. Teens NEVER pay. */}
        {(user?.role || '').toLowerCase() === 'teen' && (
          <div
            className="mb-6 p-4 sm:p-5 rounded-2xl border-2 max-w-3xl mx-auto flex items-start gap-3"
            style={{ background: 'linear-gradient(90deg, #FFF7ED 0%, #FEF2F2 100%)', borderColor: '#F59E0B' }}
            data-testid="teen-parent-approval-banner"
          >
            <span className="text-2xl flex-shrink-0">🛡️</span>
            <div className="flex-1 text-left">
              <p className="text-sm font-bold text-gray-900">Parent confirmation needed</p>
              <p className="text-xs text-gray-700 mt-0.5">
                When you tap an upgrade, we&apos;ll <strong>text your parent/guardian</strong> a YES/NO link. They review the plan and pay from their card — <strong>not yours</strong>. Nothing is charged unless they approve.
              </p>
            </div>
          </div>
        )}

        {/* Founding Families launch offer — parents only, auto-applied at checkout */}
        {founding?.active && !isAdultMode && !isTeenAccount && (
          <div className="mb-6 p-4 sm:p-5 bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 border-2 border-emerald-300 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-3 max-w-3xl mx-auto" data-testid="founding-families-banner">
            <div className="flex items-center gap-3">
              <span className="text-2xl">🏅</span>
              <div>
                <p className="text-sm font-bold text-gray-900">
                  Founding Families — first {founding.cap} only: extra {founding.percent_off}% off the Brave annual plan
                </p>
                <p className="text-xs text-gray-600 mt-0.5">
                  <strong className="text-emerald-700">£{(founding.price_pence / 100).toFixed(2)}/year</strong>
                  {' '}<span className="line-through text-gray-400">£336 on monthly</span> — under £16/month for your first year.
                  Applied automatically at checkout until {new Date(founding.ends_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}.
                </p>
              </div>
            </div>
            <span className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-extrabold rounded-full whitespace-nowrap" data-testid="founding-places-left">
              {founding.remaining} places left
            </span>
          </div>
        )}
        {user?.founding_family && (
          <div className="mb-6 max-w-3xl mx-auto text-center" data-testid="founding-family-badge">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 border border-amber-300 text-amber-800 text-sm font-bold rounded-full">
              🏅 You&apos;re a HiBFF Founding Family — thank you for being one of the first
            </span>
          </div>
        )}

        {/* Family-discount banner — identical copy to the Homepage banner,
            with a live multi-teen stepper added on the right (dashboard-only
            convenience so parents can preview their real family price). */}
        {/* Family-discount banner — hidden in adult mode (single-user account) */}
        {familyPricingMeta && !isAdultMode && (
          <div className="mb-10 p-4 sm:p-5 bg-gradient-to-r from-orange-50 via-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-4 max-w-3xl mx-auto" data-testid="family-pricing-stepper">
            <div className="flex items-center gap-3 flex-1">
              <Users size={20} style={{ color: '#F5831F' }} className="flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-bold text-gray-900">Got more than one teen at home?</p>
                <p className="text-xs text-gray-600 mt-0.5">
                  Every additional teen <strong className="text-orange-700">{familyPricingMeta.additional_teen_discount_pct}% off</strong>, all on one bill
                  <br />
                  — exclusive to the <strong className="font-bold text-gray-900">Brave</strong> annual plan.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-white rounded-xl p-1 shadow-sm">
              <button
                onClick={() => setTeenCount(Math.max(1, teenCount - 1))}
                disabled={teenCount <= 1}
                data-testid="family-stepper-minus"
                className="w-9 h-9 rounded-lg hover:bg-gray-100 disabled:opacity-40 flex items-center justify-center"
                aria-label="Fewer teens"
              >
                <Minus size={16} />
              </button>
              <div className="px-3 text-center min-w-[70px]">
                <p className="text-xl font-bold text-gray-900" data-testid="family-stepper-count">{teenCount}</p>
                <p className="text-[10px] text-gray-500 uppercase tracking-wide">{teenCount === 1 ? 'Teen' : 'Teens'}</p>
              </div>
              <button
                onClick={() => setTeenCount(Math.min(10, teenCount + 1))}
                disabled={teenCount >= 10}
                data-testid="family-stepper-plus"
                className="w-9 h-9 rounded-lg hover:bg-gray-100 disabled:opacity-40 flex items-center justify-center"
                aria-label="More teens"
              >
                <Plus size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Community family discount — 50% off in licensed HiBFF Schools cities */}
        <div className="max-w-md mx-auto mb-8 text-center" data-testid="community-code-section">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Is your city a HiBFF Schools partner?</p>
          <div className="flex gap-2 justify-center">
            <input
              value={communityCode}
              onChange={(e) => { setCommunityCode(e.target.value.toUpperCase()); setCommunityStatus(null); }}
              onBlur={checkCommunityCode}
              placeholder="Community code (e.g. AUSTIN50-XXXX)"
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-[#F5831F]"
              data-testid="community-code-input"
            />
            <button
              onClick={checkCommunityCode}
              className="px-4 py-2 text-sm font-semibold text-white rounded-lg"
              style={{ backgroundColor: '#F5831F' }}
              data-testid="community-code-apply"
            >
              Apply
            </button>
          </div>
          {communityStatus?.valid && (
            <p className="text-sm font-semibold mt-2" style={{ color: '#0F8A4B' }} data-testid="community-code-valid">
              ✓ {communityStatus.percent_off}% community discount applied — thanks to {communityStatus.school_name} in {communityStatus.city}!
            </p>
          )}
          {communityStatus?.error && (
            <p className="text-sm mt-2 text-red-600" data-testid="community-code-error">{communityStatus.error}</p>
          )}
        </div>

        {/* Sprint 207e — teen-aware container.
            For TEENS: grid is rendered blurred + non-interactive under a
            frosted-glass mask with a single "Request Upgrade" CTA. The teen
            never sees prices or selects a plan — the parent picks on landing.
            For parents/admins: grid renders normally with per-plan buttons. */}
        {(() => {
          const isTeen = isTeenAccount;
          // A teen already on a parent-paid plan sees the plan grid clearly
          // (features visible, taps route through parent-approval requests) —
          // the frosted "Parent approval required" mask is only for teens
          // still on the free First Steps tier (first upgrade).
          const teenNeedsMask = isTeen && (!currentPlanId || currentPlanId === 'free');
          const requestSent = isTeen && subscribeResult?.type === 'success';
          return (
            <div className="relative" data-testid="pricing-plan-wrapper">
              {recommendedPlanId && !isAdultMode && HOMEPAGE_PLANS.some(p => p.id === recommendedPlanId) && (
                <div className="mb-8 rounded-2xl px-5 py-4 flex items-start gap-3" style={{ background: '#FFF5EA', border: '1px solid #F5831F55' }} data-testid="recommended-plan-banner">
                  <Sparkles size={20} className="mt-0.5 flex-shrink-0" style={{ color: '#F5831F' }} />
                  <div>
                    <p className="text-sm font-bold" style={{ color: '#1A1209' }}>
                      Recommended for your family: {HOMEPAGE_PLANS.find(p => p.id === recommendedPlanId)?.badgeLabel} — {HOMEPAGE_PLANS.find(p => p.id === recommendedPlanId)?.price}{HOMEPAGE_PLANS.find(p => p.id === recommendedPlanId)?.period}
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: '#7A6B55' }}>
                      The plan most parents pick — it unlocks the full 20-challenge journey, daily AvA practice and the NFT Vault for your teen.
                    </p>
                  </div>
                </div>
              )}
              <div
                className={teenNeedsMask ? 'pointer-events-none select-none filter blur-[5px] opacity-90 transition' : ''}
                aria-hidden={teenNeedsMask ? 'true' : 'false'}
                data-testid="pricing-plan-grid"
              >
                {/* ── IDENTICAL grid markup as Homepage.js § Section 12 ── */}
                {/* Phase C — Adult-mode swaps the entire tier array. */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4" style={{ gap: '20px' }}>
                  {(isAdultMode ? ADULT_PLANS : HOMEPAGE_PLANS).map(plan => {
                    const isCurrentPlan = currentPlanId === plan.id;
                    const breakdown = livePricing[plan.id];
                    const apiPlan = (apiPlans || []).find(p => p.id === plan.id);
                    const showFamilyTotal = breakdown && (apiPlan?.price ?? 0) > 0 && teenCount > 1;

                    return (
                      <div
                        key={plan.name}
                        className={`p-5 md:p-6 bg-white rounded-2xl border-2 ${plan.free ? 'border-emerald-400 shadow-lg' : plan.popular ? 'border-purple-400 shadow-lg' : plan.annual ? 'border-amber-300' : 'border-gray-200'} relative flex flex-col ${isCurrentPlan ? 'ring-2 ring-green-500 ring-offset-2' : recommendedPlanId === plan.id ? 'ring-2 ring-[#7B4FD8] ring-offset-2' : ''}`}
                        data-testid={`pricing-card-${plan.id}`}
                      >
                        <span
                          className={`absolute -top-4 left-1/2 -translate-x-1/2 px-5 py-1.5 text-sm font-extrabold tracking-[0.18em] rounded-full whitespace-nowrap shadow-sm ${plan.badgeStyle}`}
                          data-testid={`badge-${plan.id}`}
                        >
                          {plan.badgeLabel}
                        </span>

                {/* Tagline — fixed 2-line slot so all 4 prices align */}
                <h3
                  className="font-bold text-gray-800 mt-4 text-center text-lg leading-snug min-h-[3.25rem] flex items-center justify-center px-2"
                  style={{ fontFamily: 'Georgia, serif' }}
                  data-testid={`tagline-${plan.id}`}
                >
                  {plan.tagline}
                </h3>

                {/* Price row — same vertical position on every card */}
                <div className="text-center mb-1 mt-2">
                  <span className="text-3xl md:text-4xl font-bold text-gray-900">{plan.price}</span>
                  <span className="text-gray-500 text-sm">{plan.period}</span>
                </div>

                {/* Localized lucky pricing is shown directly — spacer keeps card heights aligned */}
                <p className="text-[11px] text-gray-400 text-center min-h-[1rem]" data-testid={`local-price-${plan.id}`}>{'\u00A0'}</p>

                {/* Live multi-teen total (logged-in parents with >1 teen) */}
                {showFamilyTotal && (
                  <p className="text-[11px] font-semibold text-center flex items-center justify-center gap-1" style={{ color: '#0F8A4B' }} data-testid={`extra-teen-${plan.id}`}>
                    <Users size={11} className="flex-shrink-0" />
                    <span>For {teenCount} teens · Total <strong data-testid={`family-total-${plan.id}`}>{fmtMoney(breakdown.total_price, breakdown.currency, breakdown.interval)}</strong></span>
                  </p>
                )}

                {/* Division line between price and features */}
                <hr className="border-t border-black my-3" />

                {/* Features list — IDENTICAL markup to Homepage */}
                <ul className="mb-5 flex-1" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {plan.features.map(f => (
                    <li key={f} className="flex items-start gap-2" style={{ fontSize: '13px', lineHeight: '1.55', color: '#444444' }}>
                      <Check size={14} className="text-green-500 flex-shrink-0 mt-0.5" />{f}
                    </li>
                  ))}
                  {plan.excluded?.map(f => (
                    <li key={f} className="flex items-start gap-2" style={{ fontSize: '13px', lineHeight: '1.55', color: '#9CA3AF' }}>
                      <span className="w-3 h-3 rounded-full border border-gray-300 flex-shrink-0 mt-1" />{f}
                    </li>
                  ))}
                </ul>

                {/* CTA button — when it's the current plan we render a flat
                    green "✓ Current Plan" pill (same vertical slot, no badge
                    overlap). Otherwise the homepage CTA style/colour. */}
                {isCurrentPlan ? (
                  <button
                    disabled
                    className="w-full rounded-xl font-bold bg-green-100 text-green-700 cursor-default flex items-center justify-center gap-2"
                    style={{ minHeight: '48px', fontSize: '14px' }}
                    data-testid={`pricing-cta-${plan.id}`}
                  >
                    <Check size={16} /> Current Plan
                  </button>
                ) : (
                  <button
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={subscribing === plan.id}
                    className={`w-full rounded-xl font-bold ${plan.ctaStyle} disabled:opacity-60 flex items-center justify-center gap-2`}
                    style={{ minHeight: '48px', fontSize: '14px' }}
                    data-testid={`pricing-cta-${plan.id}`}
                  >
                    {subscribing === plan.id ? (
                      <><Loader2 size={16} className="animate-spin" /> Processing…</>
                    ) : (
                      (user?.role || '').toLowerCase() === 'teen' && plan.id !== 'free'
                        ? <>🛡️ Ask Parent to Upgrade</>
                        : plan.cta
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>
              </div>

              {/* Frosted overlay + single Request-Upgrade CTA — free-tier TEENS ONLY */}
              {teenNeedsMask && (
                <div
                  className="absolute inset-0 flex items-center justify-center p-4 sm:p-8 rounded-3xl"
                  style={{
                    background: 'linear-gradient(180deg, rgba(255,255,255,0.55) 0%, rgba(255,247,237,0.78) 100%)',
                    backdropFilter: 'blur(6px)',
                    WebkitBackdropFilter: 'blur(6px)',
                  }}
                  data-testid="teen-upgrade-mask"
                >
                  <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-6 sm:p-8 text-center border-2" style={{ borderColor: '#F59E0B' }}>
                    <div className="text-5xl mb-3">🛡️</div>
                    <h2 className="text-2xl font-extrabold text-gray-900">Parent approval required</h2>
                    <p className="text-sm text-gray-600 mt-2 mb-5">
                      Upgrades are chosen and paid by your parent or guardian. Tap below and we&apos;ll text them a link — they&apos;ll pick the plan and pay from their own card. <strong>You won&apos;t see prices or pay anything.</strong>
                    </p>
                    {requestSent ? (
                      <div
                        className="rounded-2xl p-4 border"
                        style={{ background: '#ECFDF5', borderColor: '#A7F3D0' }}
                        data-testid="teen-request-sent"
                      >
                        <p className="text-sm font-bold text-emerald-800">✓ Request sent</p>
                        <p className="text-xs text-emerald-700 mt-1">{subscribeResult.message}</p>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleSubscribe('teen_generic_upgrade')}
                        disabled={subscribing === 'teen_generic_upgrade'}
                        className="w-full py-4 bg-gradient-to-r from-orange-500 to-rose-500 text-white font-bold rounded-2xl shadow-lg hover:shadow-xl active:scale-[0.99] transition disabled:opacity-60 flex items-center justify-center gap-2"
                        data-testid="teen-request-upgrade-btn"
                      >
                        {subscribing === 'teen_generic_upgrade'
                          ? (<><Loader2 size={18} className="animate-spin" /> Sending…</>)
                          : (<>🛡️ Request an Upgrade — Ask My Parent</>)}
                      </button>
                    )}
                    <p className="text-[11px] text-gray-400 mt-3">Nothing is charged unless your parent explicitly approves.</p>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Meet the 9 AvAs — families only */}
        {!isAdultMode && <MeetTheAvAs />}

        {/* Full plan comparison grid (Phase 1 spec) — families only */}
        {!isAdultMode && !isTeenAccount && <PlanComparisonTable />}

        {/* 7-Day Free Trial — dashboard-only convenience */}
        {canTrial && (
          <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-2xl text-center" data-testid="free-trial-banner">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Clock size={22} className="text-blue-600" />
              <h3 className="text-xl font-bold text-gray-900">Not Ready to Commit?</h3>
            </div>
            <p className="text-gray-600 mb-4 max-w-lg mx-auto">
              Try HiBFF Plus free for 7 days. Full access to all features — no payment required. Cancel anytime.
            </p>
            <button
              onClick={handleStartTrial}
              disabled={startingTrial}
              className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 flex items-center justify-center gap-2 mx-auto"
              data-testid="start-trial-btn"
            >
              {startingTrial ? <><Loader2 size={18} className="animate-spin" /> Starting...</> : <>Start Your Free 7-Day Trial <ArrowRight size={18} /></>}
            </button>
            <p className="text-xs text-gray-400 mt-2">No credit card needed. You&apos;ll be upgraded to Plus instantly.</p>
          </div>
        )}

        <div className={`mt-10 p-4 border rounded-xl text-center ${isAdultMode ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`} data-testid="extra-credits-callout">
          <div className="flex items-center justify-center gap-2 mb-2">
            {isAdultMode
              ? <Award size={20} className="text-amber-500" />
              : <Heart size={20} className="text-red-500" fill="currentColor" />
            }
            <span className="font-semibold text-gray-900">
              {isAdultMode ? '🚀 Want to Practice More This Month?' : 'Want to Practice More This Month?'}
            </span>
          </div>
          <p className="text-sm text-gray-600">
            All plans can purchase {creditsLabel} for additional practice time. Visit the{' '}
            <button
              onClick={() => navigate(isAdultMode ? '/hearts?view=pro-credits' : '/hearts')}
              className={`font-semibold hover:underline ${isAdultMode ? 'text-amber-700' : 'text-red-600'}`}
              data-testid="hearts-store-link"
            >
              {isAdultMode ? 'Pro Packs' : 'Hearts Store'}
            </button> anytime.
          </p>
          {!isAdultMode && (
            <p className="text-xs text-gray-500 mt-2" data-testid="hearts-pack-prices">
              Spark {getLocalPrice('pack_small').text} (60 min) · Growth {getLocalPrice('pack_medium').text} (240 min) · Champion {getLocalPrice('pack_large').text} (600 min) — packs work with any plan. Never run out of voice practice.
            </p>
          )}
        </div>

        <div className="mt-12 p-6 bg-gray-50 rounded-2xl text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Have questions?</h3>
          <p className="text-gray-600 mb-4">Check our Help Center or contact support for personalized assistance.</p>
          <button onClick={() => navigate('/help')} className="px-6 py-2 bg-white text-gray-700 font-semibold rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all" data-testid="pricing-help-link">
            Visit Help Center
          </button>
        </div>
      </div>
    </Wrapper>
  );
};

export default PricingPage;
